import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/rootReducers';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { playNotificationSound } from '../Utils/notificationSound';

/**
 * Global hook to listen for new chat messages and play notification sound
 * Works across all screens/pages in the app
 */
const useGlobalChatNotifications = () => {
  const currentUser = useSelector((state: RootState) => state.user?.user);
  const currentUserId = currentUser?._id || currentUser?.id;
  const previousChatsRef = useRef<Map<string, { lastMessageAt: any; lastMessageSenderId?: string }>>(new Map());

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    // Helper function to get timestamp value
    const getTimestampValue = (timestamp: any): number => {
      if (!timestamp) return 0;
      if (timestamp.toMillis) return timestamp.toMillis();
      if (timestamp.toDate) return timestamp.toDate().getTime();
      if (timestamp instanceof Date) return timestamp.getTime();
      if (typeof timestamp === 'number') return timestamp;
      return 0;
    };

    // Reset previous chats when user changes
    previousChatsRef.current = new Map();

    // Listen to all chats where current user is a participant
    const chatsQuery = query(
      collection(db, 'Chats'),
      where('participants', 'array-contains', currentUserId)
    );

    const unsubscribe = onSnapshot(chatsQuery, (snapshot: any) => {
      const chatList = snapshot.docs.map((doc: any) => {
        const data = doc.data();
        return {
          id: doc.id,
          lastMessageAt: data.lastMessageAt,
          lastMessageSenderId: data.lastMessageSenderId,
        };
      });

      // Check for new messages and play notification sound
      const previousChats = previousChatsRef.current;
      const isFirstLoad = previousChats.size === 0;

      if (!isFirstLoad) {
        // Check if any chat has a new message from another user
        for (const chat of chatList) {
          const previousChat = previousChats.get(chat.id);
          if (previousChat) {
            const previousTime = getTimestampValue(previousChat.lastMessageAt);
            const currentTime = getTimestampValue(chat.lastMessageAt);
            const isNewMessage = currentTime > previousTime;
            const isFromOtherUser = chat.lastMessageSenderId !== currentUserId;

            if (isNewMessage && isFromOtherUser) {
              playNotificationSound();
              break; // Play once even if multiple chats have new messages
            }
          }
        }
      }

      // Update previous chats ref
      previousChatsRef.current = new Map(
        chatList.map((chat: any) => [
          chat.id,
          {
            lastMessageAt: chat.lastMessageAt,
            lastMessageSenderId: chat.lastMessageSenderId,
          },
        ])
      );
    });

    return () => {
      unsubscribe();
    };
  }, [currentUserId]);
};

export default useGlobalChatNotifications;

